import { forwardRef } from 'react';

import { cn } from '@/shared/utils/cn/cn.util';

import { fieldVariants } from '../field/field.tokens';

import { type DatePickerProps } from './DatePicker.types';

/**
 * A date field on the NATIVE `<input type="date">`.
 *
 * Deliberate, not a shortcut. The native control already gives us the calendar
 * popover, month/year navigation, locale-appropriate display order, the mobile
 * date wheel, and full keyboard operation — all of which a hand-built calendar
 * has to re-earn, and all of which it usually gets subtly wrong in RTL. The
 * portal ships no calendar library for exactly this reason.
 *
 * The VALUE is always ISO `YYYY-MM-DD`, whatever the browser displays: that is
 * the native contract and it is also what every date-only field in
 * `@aysar/contracts` stores. A `Date` object would drag a timezone into a value
 * that has none — a birth date is not an instant.
 *
 * Its one real limitation is worth writing down: no Hijri calendar. When the
 * product needs one, it becomes a real popover component behind this same
 * `value`/`onValueChange` contract, and nothing calling it changes.
 */
export const DatePicker = forwardRef<HTMLInputElement, DatePickerProps>(function DatePicker(
  { className, value, onValueChange, isInvalid = false, ...props },
  ref,
) {
  return (
    <input
      ref={ref}
      type="date"
      // `?? ''` keeps the input controlled: `value={null}` would hand control
      // back to the DOM mid-life and React would warn.
      value={value ?? ''}
      onChange={(event) => {
        const next = event.target.value;
        // An empty native date input reports `''`; the form wants `null`, so an
        // optional date can genuinely be removed rather than saved as "".
        onValueChange(next === '' ? null : next);
      }}
      aria-invalid={isInvalid ? true : undefined}
      className={cn(fieldVariants(), 'items-center', className)}
      {...props}
    />
  );
});
